import React, { useState, useEffect } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";

const MicPanel = ({
  micState,
  errorMessage,
  inputLevel, // 0 - 100 (Microphone input)
  outputLevel, // 0 - 100 (Speaker/Backing track master output)
  onStart,
  onStop,
}) => {
  const [uptime, setUptime] = useState(0);

  // Session uptime counter
  useEffect(() => {
    let interval = null;
    if (micState === "connected") {
      setUptime(0);
      interval = setInterval(() => {
        setUptime((prev) => prev + 1);
      }, 1000);
    } else {
      setUptime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [micState]);

  const formatUptime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
    const secs = (totalSeconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // Format status label for LCD readout
  const getStatusText = () => {
    switch (micState) {
      case "connected":
        return "SYS_ONLINE";
      case "connecting":
        return "BOOTING_SYS";
      case "denied":
        return "AUTH_FAILED";
      case "error":
        return "HARDWARE_ERR";
      default:
        return "SYS_OFFLINE";
    }
  };

  // Generate 20 segments for high-contrast vertical VU meters
  const segments = Array.from({ length: 20 }, (_, i) => 20 - i);

  // Left/Right input channel mapping
  const leftInput = inputLevel;
  const rightInput = micState === "connected" && inputLevel > 0 
    ? Math.min(100, Math.max(0, inputLevel + Math.round((Math.random() - 0.5) * 6)))
    : inputLevel;

  // Speaker Output Monitor indicators (react to master outputLevel)
  const isSpeakerSignal = micState === "connected" && outputLevel > 2;
  const isSpeakerPeak = micState === "connected" && outputLevel > 80;

  // Custom SVG Screw Component
  const Screw = () => (
    <svg className="w-4 h-4 text-muted-foreground/40 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" viewBox="0 0 20 20" fill="currentColor">
      <defs>
        <radialGradient id="screwMetal" cx="50%" cy="50%" r="50%" fx="30%" fy="30%">
          <stop offset="0%" stopColor="#8a9ab0" />
          <stop offset="50%" stopColor="#4a5568" />
          <stop offset="100%" stopColor="#1e293b" />
        </radialGradient>
      </defs>
      <circle cx="10" cy="10" r="8" fill="url(#screwMetal)" stroke="#0f172a" strokeWidth="0.75" />
      <circle cx="10" cy="10" r="5" stroke="#1e293b" strokeWidth="0.5" fill="none" opacity="0.3" />
      <line x1="6" y1="10" x2="14" y2="10" stroke="#0f172a" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="10" y1="6" x2="10" y2="14" stroke="#0f172a" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );

  return (
    <div className="bg-gradient-to-b from-[#1c222b] to-[#10141a] border border-[#2e3745] rounded-xl p-5 flex flex-col h-auto lg:h-full font-fira relative shadow-2xl overflow-hidden group select-none">
      
      {/* Carbon Fiber/Fine Dots Background Pattern overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#1e293b_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none" />

      {/* Skeuomorphic corner metal mounting screws */}
      <div className="absolute top-2.5 left-2.5 z-10"><Screw /></div>
      <div className="absolute top-2.5 right-2.5 z-10"><Screw /></div>
      <div className="absolute bottom-2.5 left-2.5 z-10"><Screw /></div>
      <div className="absolute bottom-2.5 right-2.5 z-10"><Screw /></div>

      {/* Premium Rackmount Header */}
      <div className="text-center pt-1 pb-3 mb-4 border-b border-[#2e3745]/60 flex flex-col items-center">
        <span className="text-[8px] font-bold tracking-[0.3em] text-[#64748b] uppercase block">
          DEVELOPERNOOB &bull; PRO AUDIO SERIES
        </span>
        <div className="flex items-center space-x-2 mt-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
            <div className={cn(
              "w-1.5 h-1.5 rounded-full transition-all duration-300",
              micState === "connected" ? "bg-primary animate-pulse shadow-[0_0_8px_var(--color-primary)]" : "bg-muted-foreground/30"
            )} />
          </div>
          <h2 className="text-xs font-black uppercase text-foreground tracking-widest font-fira">
            VOCAL INPUT CONSOLE
          </h2>
        </div>
        <span className="text-[7px] text-muted-foreground/60 font-mono mt-0.5">MODEL DN-ECHO-X1 // SER. 044-88A</span>
      </div>

      {/* Stacked Layout Sections: Column View */}
      <div className="flex flex-col gap-4 mb-4">
        
        {/* Section 1: LCD Diagnostics Monitor */}
        <div className="relative bg-[#04070a] border border-[#232d3a] rounded-lg p-3 font-mono overflow-hidden shadow-inner min-h-[95px] w-full">
          {/* CRT Screen Reflection Overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none" />
          {/* CRT Scanline effect */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.3)_50%)] bg-[length:100%_4px] pointer-events-none" />
          
          <div className="relative text-[9px] text-[#00ff66] leading-relaxed tracking-wider flex flex-col justify-between h-full">
            <div>
              <div className="flex justify-between border-b border-[#1b2a20]/60 pb-1 mb-1">
                <span className="text-[#00ff66]/50">CONSOLE:</span>
                <span className={cn(
                  "font-bold font-jetbrains tracking-widest",
                  micState === "connected" ? "text-[#00ff66] drop-shadow-[0_0_3px_#00ff66]" : "text-[#eab308] drop-shadow-[0_0_3px_#eab308]"
                )}>{getStatusText()}</span>
              </div>
              <div className="flex justify-between text-[8px]">
                <span className="text-[#00ff66]/50">SAMPLING:</span>
                <span>{micState === "connected" ? "48.0 kHz @ 32b" : "INACTIVE"}</span>
              </div>
              <div className="flex justify-between text-[8px]">
                <span className="text-[#00ff66]/50">LATENCY:</span>
                <span>{micState === "connected" ? "3.2 ms (ULTRA)" : "0.0 ms"}</span>
              </div>
            </div>
            
            <div className="flex justify-between border-t border-[#1b2a20]/40 pt-1 mt-1 text-[8px]">
              <span className="text-[#00ff66]/50">UPTIME:</span>
              <span className="font-bold font-jetbrains text-[#00ff66] tracking-widest drop-shadow-[0_0_2px_#00ff66]">
                {formatUptime(uptime)}
              </span>
            </div>
          </div>
        </div>

        {/* Section 2: Speaker Output Indicator Lamps (Reacts to Output sound/master outputLevel) */}
        <div className="bg-[#0f141b]/60 border border-[#232d3a]/60 rounded-lg p-2.5 flex flex-col justify-between shadow-inner w-full">
          <span className="text-[7px] font-bold text-muted-foreground uppercase tracking-widest block text-center mb-1.5 border-b border-[#2e3745]/40 pb-0.5">
            SPEAKER OUTPUT MONITOR
          </span>
          <div className="flex flex-row items-center justify-around gap-2 px-1">
            {/* SPK (Speaker Signal) Lamp */}
            <div className="flex flex-col items-center">
              <div className="w-3.5 h-3.5 rounded-full bg-black border border-[#2e3745] flex items-center justify-center p-0.5">
                <div className={cn(
                  "w-2 h-2 rounded-full transition-all duration-100",
                  isSpeakerSignal 
                    ? "bg-[#00ff66] shadow-[0_0_8px_#00ff66,inset_0_1px_1px_rgba(255,255,255,0.6)]" 
                    : "bg-[#0b2915]"
                )} />
              </div>
              <span className="text-[7px] font-bold text-muted-foreground/80 mt-0.5">SPK</span>
            </div>

            {/* CLIP (Speaker Peak) Lamp */}
            <div className="flex flex-col items-center">
              <div className="w-3.5 h-3.5 rounded-full bg-black border border-[#2e3745] flex items-center justify-center p-0.5">
                <div className={cn(
                  "w-2 h-2 rounded-full transition-all duration-75",
                  isSpeakerPeak 
                    ? "bg-[#ef4444] shadow-[0_0_8px_#ef4444,inset_0_1px_1px_rgba(255,255,255,0.6)] animate-ping" 
                    : "bg-[#ef4444] opacity-25"
                )} />
              </div>
              <span className="text-[7px] font-bold text-muted-foreground/80 mt-0.5">CLIP</span>
            </div>

            {/* LINK Active Lamp */}
            <div className="flex flex-col items-center">
              <div className="w-3.5 h-3.5 rounded-full bg-black border border-[#2e3745] flex items-center justify-center p-0.5">
                <div className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  micState === "connected"
                    ? "bg-[#38bdf8] shadow-[0_0_8px_#38bdf8,inset_0_1px_1px_rgba(255,255,255,0.6)]"
                    : "bg-[#07364b]"
                )} />
              </div>
              <span className="text-[7px] font-bold text-muted-foreground/80 mt-0.5">LINK</span>
            </div>

            {/* PWR Lamp */}
            <div className="flex flex-col items-center">
              <div className="w-3.5 h-3.5 rounded-full bg-black border border-[#2e3745] flex items-center justify-center p-0.5">
                <div className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  micState === "connected" || micState === "connecting"
                    ? "bg-[#f59e0b] shadow-[0_0_8px_#f59e0b,inset_0_1px_1px_rgba(255,255,255,0.6)]"
                    : "bg-[#451a03]"
                )} />
              </div>
              <span className="text-[7px] font-bold text-muted-foreground/80 mt-0.5">PWR</span>
            </div>
          </div>
        </div>

      </div>

      {/* Revamped Clean Vertical VU Level Monitor */}
      <div className="flex-1 flex flex-col justify-end w-full pt-4 border-t border-[#2e3745]/60 mb-5">
        <div className="flex items-center justify-center space-x-1.5 mb-3.5">
          <Icon name="BarChart2" size={10} className="text-[#64748b]" />
          <span className="text-[8px] font-bold text-muted-foreground/80 uppercase tracking-[0.2em] block">
            VERTICAL VU INPUT LEVEL
          </span>
        </div>
        
        {/* Recessed Glass Meter Bay */}
        <div className="flex justify-center items-stretch h-56 bg-[#05080c] border border-[#1e2530] rounded-lg p-3.5 shadow-[inset_0_2px_8px_rgba(0,0,0,0.95)] relative overflow-hidden select-none">
          {/* Subtle reflection overlay on glass meter */}
          <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

          {/* L Channel Column */}
          <div className="flex flex-col justify-between w-4 h-full bg-[#020406] border border-[#1a2332]/50 rounded-[4px] p-0.5 relative shadow-[inset_0_1px_3px_rgba(0,0,0,0.8)]">
            {segments.map((s) => {
              const active = leftInput >= (s / 20) * 100;
              let color = "bg-[#0c120e]"; // Inactive dark green
              if (active) {
                if (s > 18) color = "bg-[#ef4444] shadow-[0_0_8px_#ef4444,inset_0_1px_1px_rgba(255,255,255,0.4)]"; // Red peak
                else if (s > 15) color = "bg-[#f59e0b] shadow-[0_0_8px_#f59e0b,inset_0_1px_1px_rgba(255,255,255,0.4)]"; // Amber warn
                else color = "bg-[#10b981] shadow-[0_0_8px_#10b981,inset_0_1px_1px_rgba(255,255,255,0.4)]"; // Green normal
              }
              return (
                <div
                  key={`l-${s}`}
                  className={cn(
                    "flex-grow w-full rounded-[1px] transition-all duration-75 mb-[1px] last:mb-0",
                    color
                  )}
                />
              );
            })}
            <span className="absolute -bottom-5 left-0 right-0 text-[8px] text-center text-muted-foreground font-mono font-black tracking-wider opacity-60">L</span>
          </div>

          {/* High-Visibility dB Labels Column */}
          <div className="flex flex-col justify-between text-[9px] font-mono text-center py-0.5 w-14 select-none font-bold">
            <span className="text-[#ef4444] drop-shadow-[0_0_2px_rgba(239,68,68,0.2)]">+6 dB</span>
            <span className="text-[#f59e0b] drop-shadow-[0_0_2px_rgba(245,158,11,0.2)]">+3 dB</span>
            <span className="text-[#10b981] drop-shadow-[0_0_2px_rgba(16,185,129,0.2)]">0 dB</span>
            <span className="text-slate-300">-3 dB</span>
            <span className="text-slate-300">-6 dB</span>
            <span className="text-slate-400">-12 dB</span>
            <span className="text-slate-500">-24 dB</span>
            <span className="text-slate-600">-inf</span>
          </div>

          {/* R Channel Column */}
          <div className="flex flex-col justify-between w-4 h-full bg-[#020406] border border-[#1a2332]/50 rounded-[4px] p-0.5 relative shadow-[inset_0_1px_3px_rgba(0,0,0,0.8)]">
            {segments.map((s) => {
              const active = rightInput >= (s / 20) * 100;
              let color = "bg-[#0c120e]"; // Inactive dark green
              if (active) {
                if (s > 18) color = "bg-[#ef4444] shadow-[0_0_8px_#ef4444,inset_0_1px_1px_rgba(255,255,255,0.4)]";
                else if (s > 15) color = "bg-[#f59e0b] shadow-[0_0_8px_#f59e0b,inset_0_1px_1px_rgba(255,255,255,0.4)]";
                else color = "bg-[#10b981] shadow-[0_0_8px_#10b981,inset_0_1px_1px_rgba(255,255,255,0.4)]";
              }
              return (
                <div
                  key={`r-${s}`}
                  className={cn(
                    "flex-grow w-full rounded-[1px] transition-all duration-75 mb-[1px] last:mb-0",
                    color
                  )}
                />
              );
            })}
            <span className="absolute -bottom-5 left-0 right-0 text-[8px] text-center text-muted-foreground font-mono font-black tracking-wider opacity-60">R</span>
          </div>

        </div>
        <div className="h-4" /> {/* Spacing spacer for L/R letters */}
      </div>

      {/* Error diagnostics banner */}
      {errorMessage && (
        <div className="p-3 mb-4 bg-destructive/10 border border-destructive/20 rounded-md text-[9px] text-destructive leading-relaxed font-source">
          <div className="flex items-center space-x-1.5 mb-1 font-bold uppercase text-destructive-foreground">
            <Icon name="AlertTriangle" size={10} />
            <span>SYSTEM DIAGNOSTIC ALERT:</span>
          </div>
          <span className="opacity-90">{errorMessage}</span>
        </div>
      )}

      {/* Main Physical Toggle Switch: Placed at the bottom of the section */}
      <div className="w-full pt-3 border-t border-[#2e3745]/40 mt-auto">
        {micState !== "connected" ? (
          <button
            onClick={onStart}
            disabled={micState === "connecting"}
            className={cn(
              "w-full py-3.5 px-6 rounded-lg font-bold tracking-[0.15em] text-[10px] flex items-center justify-center space-x-2 border transition-all duration-200 select-none shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.5)] cursor-pointer active:translate-y-0.5 active:shadow-inner",
              micState === "connecting"
                ? "bg-[#eab308]/10 border-[#eab308]/30 text-[#eab308] cursor-wait"
                : "bg-gradient-to-b from-[#1b3a24] to-[#0c1f13] border-[#10b981]/40 text-[#10b981] hover:text-[#00ff66] hover:border-[#00ff66]/60 hover:shadow-[0_0_15px_rgba(16,185,129,0.25)]"
            )}
          >
            {micState === "connecting" ? (
              <>
                <Icon name="Loader" size={12} className="animate-spin" />
                <span className="uppercase">ENGAGING AUDIO CAPTURE...</span>
              </>
            ) : (
              <>
                <Icon name="Power" size={12} className="animate-pulse" />
                <span>START AUDIOMETRIC CAPTURE</span>
              </>
            )}
          </button>
        ) : (
          <button
            onClick={onStop}
            className="w-full py-3.5 px-6 rounded-lg font-bold tracking-[0.15em] text-[10px] flex items-center justify-center space-x-2 border border-[#ef4444]/60 bg-gradient-to-b from-[#3b1818] to-[#1f0c0c] hover:from-[#4a1c1c] hover:to-[#2b0e0e] text-[#fca5a5] hover:text-[#f87171] shadow-[inset_0_1px_1px_rgba(255,255,255,0.05),0_2px_4px_rgba(0,0,0,0.5)] active:translate-y-0.5 active:shadow-inner hover:shadow-[0_0_15px_rgba(239,68,68,0.25)] transition duration-150 cursor-pointer"
          >
            <Icon name="MicOff" size={12} />
            <span>RELEASE INPUT DEVICE / POWER OFF</span>
          </button>
        )}
      </div>

    </div>
  );
};

export default MicPanel;
